import { useCallback, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { ColumnDef, OnChangeFn, SortingState } from "@tanstack/react-table";
import Table, {
  TableControls,
  TableFooter,
  buildTagColumn,
  buildActionColumn,
  buildIconTextColumn
} from "@/components/Table";
import type { IconConfig } from "@/components/Table";
import { buildExpandableCountColumn } from "@/components/Table";
import AvatarDetails from "@/components/AvatarDetails";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import DateSelect from "@/components/DateSelect";
import EmptyState from "@/components/EmptyState";
import { renderEmptyCellState } from "@/components/Table/utils";
import { MentionedText } from "@/components/AtMention";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { toFormat } from "@/lib/dateHelpers";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { dateValueTypeToISOString } from "@/features/global/components/NotesTable/noteHelpers";
import { TaskStageValues, TaskVisibilityValues } from "@/types";
import { STAGE_DISPLAY, STAGE_TAG_TYPE, getOpenTaskOverdueTagExtras } from "../_utils/taskHelpers";
import useAmplitude from "@/hooks/useAmplitude";
import { TASK_ANALYTICS_EVENTS, getTaskAnalyticsData, type TaskAnalyticsSource } from "../_utils/amplitude";
import DeleteTaskDialog from "./DeleteTaskDialog";
import TaskSideSheet from "@/features/global/components/TaskSideSheet";
import AddTaskReferenceSideSheet from "@/pages/tasks/[taskId]/_components/AddTaskReferenceSideSheet";
import { GET_TASK_QUERY } from "@/features/global/components/TaskSideSheet/queries";
import ReferencesTable, {
  getReferenceParentLink,
  getTaskReferenceParents
} from "@/features/global/components/ReferencesTable";
import { Link, type Path, useNavigate } from "@/router";
import { TASKS_QUERY, type TaskNode, type TasksQueryResponse } from "../queries";
import { UPDATE_TASK_MUTATION, type UpdateTaskResponse } from "../_hooks/mutations";
import ReferenceParentsCell from "./ReferenceParentsCell";

type TasksTableProps = {
  data?: TasksQueryResponse;
  loading: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  clearSearch: () => void;
  createHeaderPagination: (data?: any, overrides?: any) => React.ReactElement;
  createFooterPagination: (data?: any, overrides?: any) => React.ReactElement;
  stage?: string;
  setStage: (value: string | undefined) => void;
  department?: string;
  setDepartment: (value: string | undefined) => void;
  assignedToMe: boolean;
  setAssignedToMe: (value: boolean) => void;
  createdByMe: boolean;
  setCreatedByMe: (value: boolean) => void;
  overdue: boolean;
  setOverdue: (value: boolean) => void;
  unassigned: boolean;
  setUnassigned: (value: boolean) => void;
  showUnassignedChip?: boolean;
  dueBy?: string;
  setDueBy: (value: string | undefined) => void;
  orderBy: SortingState;
  setOrderBy: OnChangeFn<SortingState>;
  hasActiveFilters: boolean;
  onClearAllFilters: () => void;
  hideUserChips?: boolean;
  hideAssigneeColumn?: boolean;
  source?: TaskAnalyticsSource;
  actions?: Action[];
};

type TaskOpenSource = "name_link" | "row" | "view_button" | "row_action";

function buildDataColumns(
  isCompactLayout: boolean,
  commentsEnabled: boolean,
  onOpenTask?: (task: TaskNode, openSource: TaskOpenSource) => void,
  hideAssigneeColumn?: boolean
): ColumnDef<TaskNode, any>[] {
  const stageColumn = buildTagColumn<TaskNode>({
    accessorKey: "stage",
    header: "Stage",
    getTagLabel: (row) => STAGE_DISPLAY[row.stage] || row.stage,
    getTagType: (row) => STAGE_TAG_TYPE[row.stage] || "surface",
    getTagExtras: (row) => getOpenTaskOverdueTagExtras(row),
    size: 120,
    enableSorting: true,
    sortType: "text"
  });

  return [
    buildIconTextColumn<TaskNode>({
      accessorKey: "name",
      header: "Name",
      enableSorting: true,
      sortType: "text",
      size: 280,
      minSize: 240,
      iconPosition: "after",
      getText: (row) => {
        const count = commentsEnabled ? (row.commentsCount ?? 0) : 0;
        return (
          <>
            <Link
              to="/tasks/:taskId"
              params={{ taskId: row.id }}
              onClick={(e) => {
                e.stopPropagation();
                onOpenTask?.(row, "name_link");
              }}
            >
              {row.name ? <MentionedText content={row.name} /> : "--"}
            </Link>
            {count > 0 && (
              <span className="sr-only">
                {count} {count === 1 ? "comment" : "comments"}
              </span>
            )}
          </>
        );
      },
      getIcon: (row) => {
        const icons: IconConfig[] = [];
        if (row.visibility === TaskVisibilityValues.PRIVATE) {
          icons.push({
            name: "lock" as const,
            size: 20,
            tooltip: {
              supportingText: "Task is Private"
            }
          });
        }

        if (!commentsEnabled) return icons;
        const count = row.commentsCount ?? 0;
        if (count === 0) return icons;
        icons.push({
          name: "mode_comment",
          size: 20,
          tooltip: {
            supportingText: `${count} ${count === 1 ? "comment" : "comments"}`
          }
        });
        return icons;
      }
    }),
    ...(hideAssigneeColumn
      ? []
      : [
          {
            id: "assignedToName",
            header: isCompactLayout ? "" : "Assignee",
            size: 200,
            enableSorting: true,
            meta: { sortType: "text" as const, sortLabel: "Assignee" },
            accessorFn: (row) => row.assignedTo?.name || "",
            cell: ({ row }) => {
              const assignee = row.original.assignedTo;
              if (!assignee) {
                return renderEmptyCellState(null);
              }

              return (
                <AvatarDetails
                  picture={assignee.photo ?? undefined}
                  title={assignee.name || "--"}
                  text={assignee.email || <span className="opacity-20">--</span>}
                />
              );
            }
          } satisfies ColumnDef<TaskNode, any>
        ]),
    {
      accessorFn: (row) =>
        (row.teamTags ?? [])
          .map((tag) => tag.name)
          .filter(Boolean)
          .join(", "),
      id: "teamTags",
      header: "Teams",
      enableSorting: false,
      cell: ({ getValue }) => renderEmptyCellState(getValue()),
      size: 180
    },
    {
      id: "referenceParents",
      header: "References",
      enableSorting: false,
      size: 240,
      minSize: 180,
      accessorFn: (row) =>
        getTaskReferenceParents(row.references)
          .map((parent) => parent.name)
          .join(", "),
      cell: ({ row }) => <ReferenceParentsCell references={row.original.references} />
    },
    buildExpandableCountColumn<TaskNode>({
      accessorKey: "references",
      header: "Reference Details",
      getCount: (row) => row.references?.length || 0,
      size: 150
    }),
    {
      accessorFn: (row) => row.dueDate,
      id: "dueDate",
      header: "Due Date",
      enableSorting: true,
      meta: { sortType: "date" as const },
      cell: ({ getValue }) => renderEmptyCellState(toFormat(getValue() as string)),
      size: 140
    },
    stageColumn
  ];
}

export default function TasksTable({
  data,
  loading,
  searchValue,
  setSearchValue,
  clearSearch,
  createHeaderPagination,
  createFooterPagination,
  stage,
  setStage,
  department,
  setDepartment: _setDepartment,
  assignedToMe,
  setAssignedToMe,
  createdByMe,
  setCreatedByMe,
  overdue,
  setOverdue,
  unassigned,
  setUnassigned,
  showUnassignedChip,
  dueBy,
  setDueBy,
  orderBy,
  setOrderBy,
  hasActiveFilters,
  onClearAllFilters,
  hideUserChips,
  hideAssigneeColumn,
  source = "my_tasks_tab",
  actions = []
}: TasksTableProps) {
  const { Menu, setMenu } = useTableRowMenu();
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const canManageTasks = useCanManageTasksBool();
  const { isCompactLayout } = useIsCompactLayout();
  const [updateTask, { loading: isUpdatingTask }] = useMutation<UpdateTaskResponse>(UPDATE_TASK_MUTATION);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<TaskNode | null>(null);
  const [addReferenceTask, setAddReferenceTask] = useState<TaskNode | null>(null);

  const tasks = useMemo(
    () => (data?.tasks?.nodes || []).filter((node): node is TaskNode => node != null),
    [data?.tasks?.nodes]
  );

  const handleCloseSideSheet = () => {
    setIsSideSheetOpen(false);
    setSelectedTaskId(null);
  };

  const handleCompleteTask = useCallback(
    async (taskId: string) => {
      if (isUpdatingTask) return;

      try {
        const result = await updateTask({
          variables: { input: { id: taskId, stage: TaskStageValues.COMPLETE } }
        });

        if (result.data?.updateTask?.success) {
          const task = tasks.find((item) => item.id === taskId);
          trackEvent(TASK_ANALYTICS_EVENTS.stageChanged, {
            ...(task ? getTaskAnalyticsData(task, source, { includeStage: false }) : { taskId, source }),
            fromStage: task?.stage,
            toStage: TaskStageValues.COMPLETE
          });
          showSnackbar("Task completed");
          return;
        }

        const apiMessage = result.data?.updateTask?.message;
        if (apiMessage) {
          showErrorSnackbar("Task not completed", { subtitle: apiMessage });
        } else {
          showErrorSnackbar("Task not completed", { subtitle: "Please try again later." });
        }
      } catch {
        showErrorSnackbar("Task not completed", { subtitle: "Please try again later." });
      }
    },
    [isUpdatingTask, source, tasks, trackEvent, updateTask]
  );

  const trackFilterChange = (filter: string, value: string | boolean | undefined) => {
    trackEvent(TASK_ANALYTICS_EVENTS.listFiltered, {
      source,
      filter,
      value
    });
  };

  const handleOpenTask = useCallback(
    (task: TaskNode, openSource: TaskOpenSource) => {
      trackEvent(TASK_ANALYTICS_EVENTS.opened, {
        ...getTaskAnalyticsData(task, source),
        openSource
      });
    },
    [source, trackEvent]
  );

  const columns = useMemo(() => {
    const cols = buildDataColumns(isCompactLayout, true, handleOpenTask, hideAssigneeColumn);
    cols.push(
      buildActionColumn<TaskNode>({
        getActions: (task: TaskNode) => {
          const actions: Action[] = [];

          if (task.stage === TaskStageValues.OPEN) {
            actions.push({
              section: "Manage",
              text: "Complete Task",
              onClick: () => void handleCompleteTask(task.id)
            });
          }

          actions.push({
            section: "Actions",
            text: "View Task",
            onClick: () => {
              handleOpenTask(task, "row_action");
              navigate(`/tasks/${task.id}` as Path);
            }
          });

          if (task.stage === TaskStageValues.OPEN) {
            actions.push({
              section: "Actions",
              text: "Add Reference",
              testId: "add-reference-action",
              onClick: () => setAddReferenceTask(task)
            });
          }

          if (canManageTasks) {
            actions.push({
              text: "Delete Task",
              iconName: "delete",
              destructive: true,
              onClick: () => setTaskToDelete(task)
            });
          }

          return actions;
        },
        onViewClick: (task: TaskNode) => {
          handleOpenTask(task, "view_button");
          setSelectedTaskId(task.id);
          setIsSideSheetOpen(true);
        },
        showViewButton: () => true,
        viewButtonTitle: "View Task Details",
        actionId,
        setMenu
      })
    );
    return cols;
  }, [setMenu, canManageTasks, isCompactLayout, hideAssigneeColumn, handleCompleteTask, handleOpenTask, navigate]);

  const filters = {
    search: !!searchValue,
    stage: !!stage,
    department: !!department,
    dueBy: !!dueBy
  };
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const filterChips = [
    ...(showUnassignedChip
      ? [
          {
            label: "Unassigned",
            onClick: () => {
              const nextValue = !unassigned;
              trackFilterChange("unassigned", nextValue);
              setUnassigned(nextValue);
            },
            selected: unassigned
          }
        ]
      : []),
    ...(!hideUserChips
      ? [
          {
            label: "Assigned to Me",
            onClick: () => {
              const nextValue = !assignedToMe;
              trackFilterChange("assignedToMe", nextValue);
              setAssignedToMe(nextValue);
            },
            selected: assignedToMe
          },
          {
            label: "Created by Me",
            onClick: () => {
              const nextValue = !createdByMe;
              trackFilterChange("createdByMe", nextValue);
              setCreatedByMe(nextValue);
            },
            selected: createdByMe
          }
        ]
      : []),
    {
      label: "Overdue",
      onClick: () => {
        const nextValue = !overdue;
        trackFilterChange("overdue", nextValue);
        setOverdue(nextValue);
      },
      selected: overdue
    }
  ];

  return (
    <>
      <Menu />
      <Table
        data={tasks}
        loading={loading}
        columns={columns}
        onClickRow={(row) => {
          handleOpenTask(row.original, "row");
          navigate(`/tasks/${row.original.id}` as Path);
        }}
        hasActiveFilters={hasActiveFilters}
        serverSideSorting
        sortingState={orderBy}
        onSortingChange={setOrderBy}
        getRowCanExpand={(row) => (row.original.references?.length || 0) > 0}
        renderExpandedRow={({ row }) => (
          <ReferencesTable
            variant="details-panel"
            references={row.original.references || []}
            title={`Reference Details (${row.original.references?.length || 0})`}
            getParentLink={getReferenceParentLink}
          />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(data?.tasks)}
            actions={actions}
            activeFilters={activeFilterCount}
            onClearAllFilters={onClearAllFilters}
            onSortClick={onSortClick}
            useExpandedFilters
            expandedFiltersState={filters}
            filterChips={filterChips}
            mutuallyExclusiveChips={false}
          >
            <SearchInput
              data-name="search"
              placeholder="Search Tasks"
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
            />
            <div data-name="stage" className="w-full sm:min-w-[160px] sm:max-w-[200px]">
              <Select
                label="Stage"
                value={stage || "ALL"}
                dropdownHeight="full"
                onSelect={(value) => {
                  const nextValue = value === "ALL" ? undefined : value;
                  trackFilterChange("stage", nextValue);
                  setStage(nextValue);
                }}
                options={[
                  { value: "ALL", displayText: "Show All", border: true },
                  { value: TaskStageValues.OPEN, displayText: "Open" },
                  { value: TaskStageValues.COMPLETE, displayText: "Complete" },
                  {
                    value: TaskStageValues.NOT_DOING,
                    displayText: "Not Doing"
                  }
                ]}
              />
            </div>
            <div data-name="dueBy" data-filter-size="custom">
              <DateSelect
                label="Due By"
                useSingleDate
                value={dueBy ? { startDate: dueBy, endDate: dueBy } : { startDate: null, endDate: null }}
                onChange={(val) => {
                  const nextValue = dateValueTypeToISOString(val) ?? undefined;
                  trackFilterChange("dueBy", nextValue);
                  setDueBy(nextValue);
                }}
                placeholder="Show All"
                useModal
              />
            </div>
          </TableControls>
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            iconName="assignment_turned_in"
            title="No Tasks Yet"
            caption="There are no tasks to display at this time."
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="search" />
        )}
        renderFooter={() => <TableFooter pagination={createFooterPagination(data?.tasks)} />}
      />
      <TaskSideSheet open={isSideSheetOpen} onClose={handleCloseSideSheet} taskId={selectedTaskId} />
      {addReferenceTask ? (
        <AddTaskReferenceSideSheet
          open
          onClose={() => setAddReferenceTask(null)}
          taskId={addReferenceTask.id}
          existingReferenceIds={(addReferenceTask.references || []).map((ref) => ref.id)}
          refetchQueries={[TASKS_QUERY, GET_TASK_QUERY]}
          source={source}
        />
      ) : null}
      <DeleteTaskDialog
        open={!!taskToDelete}
        taskId={taskToDelete?.id ?? ""}
        task={taskToDelete ?? undefined}
        source={source}
        onClose={() => setTaskToDelete(null)}
        onDeleted={() => setTaskToDelete(null)}
      />
    </>
  );
}
